import { type TypographyStyle, createTheme } from '@mui/material/styles'

// notes on typography
// our current global typography styles conflict with the mui way of doing things, so some of the typography styles specified below are still being over-ridden by the global styles
// wherever possible, we should convert to the mui theme and deprecate the global typography styles

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display1: true
    display2: true
    display3: true
    body1Bold: true
    body3: true
    body4: true
    subtitle2: true
    alertTitle: true
  }
}
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    selected: true
  }
}

// extend mui type definition for palette
declare module '@mui/material/styles' {
  interface TypographyVariants {
    display1: TypographyStyle
    display2: TypographyStyle
    display3: TypographyStyle
    body1Bold: TypographyStyle
    body3: TypographyStyle
    body4: TypographyStyle
    subtitle2: TypographyStyle
    alertTitle: TypographyStyle
  }
  interface TypographyVariantsOptions {
    display1?: TypographyStyle
    display2?: TypographyStyle
    display3?: TypographyStyle
    body1Bold: TypographyStyle
    body3: TypographyStyle
    body4: TypographyStyle
    subtitle2: TypographyStyle
    alertTitle: TypographyStyle
  }

  interface PaletteColor {
    // for core palette colors
    p4?: string
    p12?: string
    p30?: string
    p50?: string
    p160?: string
    p190?: string
    // for accent colors
    50?: string
    100?: string
    200?: string
    300?: string
    400?: string
    500?: string
    600?: string
    700?: string
    800?: string
    900?: string
  }

  interface SimplePaletteColorOptions {
    p4?: string
    p12?: string
    p30?: string
    p50?: string
    p160?: string
    p190?: string
  }
  interface Palette {
    textDarkMode: PaletteColor
    brand: PaletteColor
    accentBlue: PaletteColor
    accentGreen: PaletteColor
    accentGrey: PaletteColor
    accentPink: PaletteColor
    accentOrange: PaletteColor
    accentRed: PaletteColor
    accentYellow: PaletteColor
    discordBlue: string
  }
  interface PaletteOptions {
    textDarkMode: PaletteColor
    brand: PaletteColor
    accentBlue: PaletteColor
    accentGreen: PaletteColor
    accentGrey: PaletteColor
    accentOrange: PaletteColor
    accentPink: PaletteColor
    accentRed: PaletteColor
    accentYellow: PaletteColor
    discordBlue: string
  }
}

// declare module '@mui/material/SvgIcon' {
//   interface SvgIconPropsColorOverrides {
//     brand: true
//     accentBlue: true
//     accentPink: true
//     accentOrange: true
//     discordBlue: true
//     'text.secondary': true
//   }
// }

const theme = createTheme({
  // colors defined here: https://www.figma.com/file/V9jH8bsolaFAvPlNgufpCR/Techstars-Design-System?type=design&node-id=11245-76614&t=Fvke0g9NMtnWjRMk-0
  palette: {
    // out of the box mui color options
    text: {
      primary: 'rgba(0, 0, 0, 0.87)',
      secondary: 'rgba(0, 0, 0, 0.6)',
      disabled: 'rgba(0, 0, 0, 0.38)',
    },
    primary: {
      main: 'rgba(52, 117, 178, 1)',
      light: 'rgba(107, 163, 228, 1)',
      dark: 'rgba(0, 74, 130, 1)',
      contrastText: 'rgba(255, 255, 255, 1)',
    },
    error: {
      main: 'rgba(196, 57, 74, 1)',
      dark: 'rgba(141, 0, 35, 1)',
      light: 'rgba(252, 108, 117, 1)',
      contrastText: 'rgba(255, 255, 255, 1)',
      p4: 'rgba(196, 57, 74, 0.04)',
      p12: 'rgba(196, 57, 74, 0.12)',
      p30: 'rgba(196, 57, 74, 0.3)',
      p50: 'rgba(196, 57, 74, 0.5)',
    },
    success: {
      main: 'rgba(57, 196, 171, 1)',
      dark: 'rgba(0, 147, 124, 1)',
      light: 'rgba(117, 247, 221, 1)',
      contrastText: 'rgba(255, 255, 255, 1)',
      p4: 'rgba(57, 196, 171, 0.04)',
      p12: 'rgba(57, 196, 171, 0.12)',
      p30: 'rgba(57, 196, 171, 0.3)',
      p50: 'rgba(57, 196, 171, 0.5)',
    },
    warning: {
      main: 'rgba(249, 195, 4, 1)',
      light: 'rgba(255, 245, 80, 1)',
      dark: 'rgba(193, 147, 0, 1)',
      p4: 'rgba(249, 195, 4, 0.04)',
      p12: 'rgba(249, 195, 4, 0.12)',
      p30: 'rgba(249, 195, 4, 0.3)',
      p50: 'rgba(249, 195, 4, 0.5)',
      p160: 'rgba(102, 98, 32, 1)',
    },
    accentGreen: {
      main: 'rgba(100, 185, 105, 1)', // 500
      light: 'rgb(240, 248, 240)', // 50
      dark: 'rgb(42, 78, 44)', // 900
      contrastText: 'rgba(255, 255, 255, 1)',
      50: 'rgb(240, 248, 240)', // light
      100: 'rgb(207, 233, 209)',
      200: 'rgb(184, 223, 186)',
      300: 'rgb(151, 208, 155)',
      400: 'rgb(131, 199, 135)',
      500: 'rgba(100, 185, 105, 1)', // main
      600: 'rgb(91, 168, 96)',
      700: 'rgb(71, 131, 75)',
      800: 'rgb(55, 102, 58)',
      900: 'rgb(42, 78, 44)', // dark
    },

    // Add minimal required colors to satisfy TypeScript interface
    textDarkMode: {
      main: '#ffffff',
      dark: '#cccccc',
      light: '#ffffff',
      contrastText: '#000000',
    },
    brand: {
      main: '#1976d2',
      dark: '#115293',
      light: '#42a5f5',
      contrastText: '#ffffff',
    },
    accentBlue: {
      main: '#1976d2',
      dark: '#115293',
      light: '#42a5f5',
      contrastText: '#ffffff',
    },
    accentGrey: {
      main: '#9e9e9e',
      dark: '#616161',
      light: '#f5f5f5',
      contrastText: '#ffffff',
    },
    accentPink: {
      main: '#e91e63',
      dark: '#ad1457',
      light: '#f8bbd9',
      contrastText: '#ffffff',
    },
    accentOrange: {
      main: '#ff9800',
      dark: '#f57c00',
      light: '#ffb74d',
      contrastText: '#ffffff',
    },
    accentRed: {
      main: '#f44336',
      dark: '#d32f2f',
      light: '#ef5350',
      contrastText: '#ffffff',
    },
    accentYellow: {
      main: 'rgb(242, 206, 88)', // 500
      light: 'rgb(254, 250, 238)', // 50
      dark: 'rgb(102, 87, 37)', // 900
      contrastText: 'rgba(255, 255, 255, 1)',
      50: 'rgb(254, 250, 238)', // light
      100: 'rgb(251, 240, 203)',
      200: 'rgb(249, 232, 178)',
      300: 'rgb(246, 222, 143)',
      400: 'rgb(245, 216, 121)',
      500: 'rgb(242, 206, 88)', // main
      600: 'rgb(220, 187, 80)',
      700: 'rgb(172, 146, 62)',
      800: 'rgb(133, 113, 48)',
      900: 'rgb(102, 87, 37)', // dark
    },
    discordBlue: 'rgba(88, 101, 242, 1)',
  },
  // typography system defined here: https://www.figma.com/file/V9jH8bsolaFAvPlNgufpCR/Techstars-Design-System?type=design&node-id=6605-52433&t=Fvke0g9NMtnWjRMk-0
  typography: {
    fontFamily: ['Suisse Intl', 'Helvetica', 'Arial', 'sans-serif'].join(','),
    display1: {
      fontSize: '96px',
      lineHeight: '112px',
      letterSpacing: '-1.5px',
      fontFamily: 'Suisse Intl Bold',
    },
    display2: {
      fontSize: '60px',
      lineHeight: '72px',
      letterSpacing: '-0.5px',
      fontFamily: 'Suisse Intl Bold',
    },
    display3: {
      fontSize: '48px',
      lineHeight: '56px',
      letterSpacing: 0,
      fontFamily: 'Suisse Intl Bold',
    },
    body1Bold: {
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      fontFamily: 'Suisse Intl Semibold',
    },
    body3: {
      fontSize: '12px',
      lineHeight: '17px',
      letterSpacing: '0.15px',
    },
    body4: {
      fontSize: '16px',
      lineHeight: '24px',
      letterSpacing: '0.15px',
      fontFamily: 'Suisse Intl Medium',
    },
    subtitle2: {
      fontSize: '14px',
      fontWeight: 500,
      lineHeight: '21.98px',
      letterSpacing: '0.1px',
    },
    alertTitle: {
      fontSize: '16px',
      fontWeight: 500,
      lineHeight: '24px',
      letterSpacing: '0.15px',
      fontFamily: 'Suisse Intl Medium',
    },
  },
})

export default theme
//       p12: 'rgba(249, 195, 4, 0.12)',
//       p30: 'rgba(249, 195, 4, 0.3)',
//       p50: 'rgba(249, 195, 4, 0.5)',
//       p160: 'rgba(102, 98, 32, 1)',
//     },
//     // custom color options
//     brand: {
//       main: 'rgba(57, 196, 99, 1)',
//       dark: 'rgba(40, 139, 70, 1)',
//       light: 'rgba(235, 249, 239, 1)',
//       contrastText: 'rgba(255, 255, 255, 1)',
//       p50: 'rgba(235, 249, 239, 1)',
//     },
//     textDarkMode: palette.augmentColor({
//       color: {
//         main: 'rgba(255, 255, 255, 0.7)',
//       },
//     }),
//     accentBlue: {
//       main: 'rgb(29, 136, 194)', // 500
//       light: 'rgb(232, 243, 249)', // 50
//       dark: 'rgb(12, 57, 81)', // 900
//       contrastText: 'rgba(255, 255, 255, 1)',
//       50: 'rgb(232, 243, 249)', // light
//       100: 'rgb(185, 218, 236)',
//       200: 'rgb(151, 200, 227)',
//       300: 'rgb(104, 175, 214)',
//       400: 'rgb(74, 160, 206)',
//       500: 'rgb(29, 136, 194)', // main
//       600: 'rgb(26, 124, 177)',
//       700: 'rgb(21, 97, 138)',
//       800: 'rgb(16, 75, 107)',
//       900: 'rgb(12, 57, 81)', // dark
//     },
//     accentGreen: {
//       main: 'rgba(100, 185, 105, 1)', // 500
//       light: 'rgb(240, 248, 240)', // 50
//       dark: 'rgb(42, 78, 44)', // 900
//       contrastText: 'rgba(255, 255, 255, 1)',
//       50: 'rgb(240, 248, 240)', // light
//       100: 'rgb(207, 233, 209)',
//       200: 'rgb(184, 223, 186)',
//       300: 'rgb(151, 208, 155)',
//       400: 'rgb(131, 199, 135)',
//       500: 'rgba(100, 185, 105, 1)', // main
//       600: 'rgb(91, 168, 96)',
//       700: 'rgb(71, 131, 75)',
//       800: 'rgb(55, 102, 58)',
//       900: 'rgb(42, 78, 44)', // dark
//     },
//     accentPink: {
//       main: 'rgba(238, 77, 155, 1)', // 500
//       light: 'rgb(253, 237, 245)', // 50
//       dark: 'rgb(100, 32, 65)', // 900
//       contrastText: 'rgba(255, 255, 255, 1)',
//       50: 'rgb(253, 237, 245)', // light
//       100: 'rgb(250, 200, 224)',
//       200: 'rgb(247, 173, 209)',
//       300: 'rgb(244, 136, 188)',
//       400: 'rgb(241, 113, 175)',
//       500: 'rgba(238, 77, 155, 1)', // main
//       600: 'rgb(217, 70, 141)',
//       700: 'rgb(169, 55, 110)',
//       800: 'rgb(131, 42, 85)',
//       900: 'rgb(100, 32, 65)', // dark
//     },
//     accentOrange: {
//       main: 'rgba(238, 102, 35, 1)', // 500
//       light: 'rgb(253, 240, 233)', // 50
//       dark: 'rgb(100, 43, 15)', // 900
//       contrastText: 'rgba(255, 255, 255, 1)',
//       50: 'rgb(253, 240, 233)', // light
//       100: 'rgb(250, 208, 187)',
//       200: 'rgb(247, 185, 154)',
//       300: 'rgb(244, 152, 108)',
//       400: 'rgb(241, 133, 79)',
//       500: 'rgba(238, 102, 35, 1)', // main
//       600: 'rgb(217, 93, 32)',
//       700: 'rgb(169, 72, 25)',
//       800: 'rgb(131, 56, 19)',
//       900: 'rgb(100, 43, 15)', // dark
//     },
//     accentRed: {
//       main: 'rgba(237, 28, 36, 1)', // 500
//       light: 'rgb(253, 232, 233)', // 50
//       dark: 'rgb(100, 12, 15)', // 900
//       contrastText: 'rgba(255, 255, 255, 1)',
//       50: 'rgb(253, 232, 233)', // light
//       100: 'rgb(249, 185, 187)',
//       200: 'rgb(247, 151, 154)',
//       300: 'rgb(243, 103, 108)',
//       400: 'rgb(241, 73, 80)',
//       500: 'rgba(237, 28, 36, 1)', // main
//       600: 'rgb(216, 25, 33)',
//       700: 'rgb(168, 20, 26)',
//       800: 'rgb(130, 15, 20)',
//       900: 'rgb(100, 12, 15)', // dark
//     },
//     accentYellow: {
//       main: 'rgb(242, 206, 88)', // 500
//       light: 'rgb(254, 250, 238)', // 50
//       dark: 'rgb(102, 87, 37)', // 900
//       contrastText: 'rgba(255, 255, 255, 1)',
//       50: 'rgb(254, 250, 238)', // light
//       100: 'rgb(251, 240, 203)',
//       200: 'rgb(249, 232, 178)',
//       300: 'rgb(246, 222, 143)',
//       400: 'rgb(245, 216, 121)',
//       500: 'rgb(242, 206, 88)', // main
//       600: 'rgb(220, 187, 80)',
//       700: 'rgb(172, 146, 62)',
//       800: 'rgb(133, 113, 48)',
//       900: 'rgb(102, 87, 37)', // dark
//     },
//     accentGrey: {
//       main: 'rgb(158, 158, 158)', // 500
//       light: 'rgb(250, 250, 250)', // 50
//       dark: 'rgb(33, 33, 33)', // 900
//       contrastText: 'rgba(255, 255, 255, 1)',
//       50: 'rgb(250, 250, 250)', // light
//       100: 'rgb(245, 245, 245)',
//       200: 'rgb(238, 238, 238)',
//       300: 'rgb(224, 224, 224)',
//       400: 'rgb(189, 189, 189)',
//       500: 'rgb(158, 158, 158)', // main
//       600: 'rgb(117, 117, 117)',
//       700: 'rgb(97, 97, 97)',
//       800: 'rgb(66, 66, 66)',
//       900: 'rgb(33, 33, 33)', // dark
//     },
//     // accentYellow: {},
//     // not in design system but used semi-frequently
//     discordBlue: 'rgba(88, 101, 242, 1)',
//   },
//   // typography system defined here: https://www.figma.com/file/V9jH8bsolaFAvPlNgufpCR/Techstars-Design-System?type=design&node-id=6605-52433&t=Fvke0g9NMtnWjRMk-0
//   typography: {
//     fontFamily: ['Suisse Intl', 'Helvetica', 'Arial', 'sans-serif'].join(','),
//     display1: {
//       fontSize: '96px',
//       lineHeight: '112px',
//       letterSpacing: '-1.5px',
//       fontFamily: 'Suisse Intl Bold', // 700 - fonts loaded separately by weight
//     },
//     display2: {
//       fontSize: '60px',
//       lineHeight: '72px',
//       letterSpacing: '-0.5px',
//       fontFamily: 'Suisse Intl Bold', // 700 - fonts loaded separately by weight
//     },
//     display3: {
//       fontSize: '48px',
//       lineHeight: '56px',
//       letterSpacing: 0,
//       fontFamily: 'Suisse Intl Bold', // 700 - fonts loaded separately by weight
//     },
//     h1: {
//       fontSize: '34px',
//       lineHeight: '50px',
//       letterSpacing: '.25px',
//       fontFamily: 'Suisse Intl Semibold', // 600 - fonts loaded separately by weight
//     },
//     h2: {
//       fontSize: '24px',
//       lineHeight: '32px',
//       letterSpacing: 0,
//       fontFamily: 'Suisse Intl Semibold', // 600 - fonts loaded separately by weight
//     },
//     h3: {
//       fontSize: '20px',
//       lineHeight: '32px',
//       letterSpacing: '0.15px',
//       fontFamily: 'Suisse Intl Semibold', // 600 - fonts loaded separately by weight
//     },
//     h4: {
//       fontSize: '18px',
//       lineHeight: '28.8px',
//       letterSpacing: '0.15px',
//       fontFamily: 'Suisse Intl Semibold', // 600 - fonts loaded separately by weight
//     },
//     body1: {
//       fontSize: '16px',
//       lineHeight: '24px',
//       letterSpacing: '0.15px',
//       // 400 (default) - fonts loaded separately by weight
//     },
//     body1Bold: {
//       fontSize: '16px',
//       lineHeight: '24px',
//       letterSpacing: '0.15px',
//       fontFamily: 'Suisse Intl Semibold', // 600 - fonts loaded separately by weight
//     },
//     body2: {
//       fontSize: '14px',
//       lineHeight: '20px',
//       letterSpacing: '0.17px',
//       // 400 (default) - fonts loaded separately by weight
//     },
//     body3: {
//       fontSize: '12px',
//       lineHeight: '17px',
//       letterSpacing: '0.15px',
//       // 400 (default) - fonts loaded separately by weight
//     },
//     body4: {
//       fontSize: '16px',
//       lineHeight: '24px',
//       letterSpacing: '0.15px',
//       fontFamily: 'Suisse Intl Medium', // 500 - fonts loaded separately by weight
//     },
//     subtitle2: {
//       fontSize: '14px',
//       fontWeight: 500,
//       lineHeight: '21.98px',
//       letterSpacing: '0.1px',
//     },
//     alertTitle: {
//       fontSize: '16px',
//       fontWeight: 500,
//       lineHeight: '24px',
//       letterSpacing: '0.15px',
//       fontFamily: 'Suisse Intl Medium', // 500 - fonts loaded separately by weight
//     },
//   },
//   /* default breakpoints from https://mui.com/material-ui/customization/breakpoints/ */
//   breakpoints: {
//     values: {
//       xs: 0,
//       sm: 600,
//       md: 900,
//       lg: 1200,
//       xl: 1536,
//     },
//   },
//   components: {
//     MuiCssBaseline: {
//       styleOverrides: `
//         @font-face {
//           font-family: 'Suisse Intl Bold';
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/26xBafj25tAEMQDG3YEZUJ/dd004bbfd98fb09bba791e3fdcb29fd3/SuisseIntl-Bold.woff2') format('woff2');
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/5bDdQVZt1gN5s1x2cRE2ZS/0bf5df63ed511165bf3d27c91044cae4/SuisseIntl-Bold.woff') format('woff');
//           font-weight: bold;
//           font-style: normal;
//           font-display: swap;
//         }

//         @font-face {
//           font-family: 'Suisse Intl Medium';
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/3ie9X2fbmXIQoX9BOgNiSH/c22da50201b88994d896b9ef83baa151/SuisseIntl-Medium.woff2') format('woff2');
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/ETE8guAotWkE3C9jIi8KF/812278237c23fbe90cc61a7da55e3d74/SuisseIntl-Medium.woff') format('woff');
//           font-weight: 500;
//           font-style: normal;
//           font-display: swap;
//         }

//         @font-face {
//           font-family: 'Suisse Intl Semibold';
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/9jebvMg0FSkb0r2cDnFza/a5cc83b29075fa398cb22c64037c1b0d/SuisseIntl-Semibold.woff2') format('woff2');
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/45tZb81COXvKtZ8jWMrv16/d867bd17f60679c25a8b31598806b243/SuisseIntl-Semibold.woff') format('woff');
//           font-weight: 600;
//           font-display: swap;
//         }

//         @font-face {
//           font-family: 'Suisse Intl';
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/2SnMtopXXzVU3mW4hPB4r6/3e0efc9b39d8fbab8c93a87fa8dc944f/SuisseIntl-Regular.woff2') format('woff2');
//           src: url('https://assets.ctfassets.net/98o75qsl2t3x/3kznsfHFWdisguR07tV49R/bff394e7f0cb865aeb99faca5ccd2e26/SuisseIntl-Regular.woff') format('woff');
//           font-weight: normal;
//           font-display: swap;
//         }
//       `,
//     },
//     MuiButton: {
//       styleOverrides: {
//         sizeSmall: {
//           fontSize: '13px',
//           lineHeight: '22px',
//           letterSpacing: '0.46px',
//           fontFamily: 'Suisse Intl Semibold',
//           textTransform: 'none',
//           height: '30px',
//         },
//         sizeMedium: {
//           fontSize: '14px',
//           lineHeight: '24px',
//           letterSpacing: '0.4px',
//           fontFamily: 'Suisse Intl Semibold',
//           textTransform: 'none',
//           height: '36px',
//         },
//         sizeLarge: {
//           fontSize: '16px',
//           lineHeight: '26px',
//           letterSpacing: '0.46px',
//           fontFamily: 'Suisse Intl Semibold',
//           textTransform: 'none',
//           height: '42px',
//         },
//       },
//       variants: [
//         {
//           props: { variant: 'selected' },
//           style: {
//             backgroundColor: 'rgba(52, 117, 178, .2)',
//             border: '1px solid rgb(29, 136, 194)',
//             '&:hover': {
//               borderColor: 'rgb(16, 75, 107)',
//               backgroundColor: 'rgb(232, 243, 249)',
//             },
//           },
//         },
//       ],
//     },
//     MuiAlert: {
//       // all alerts should have overrides for border radius per TU Figma design
//       styleOverrides: {
//         standardInfo: {
//           borderRadius: '10px',
//         },
//         standardWarning: {
//           borderRadius: '10px',
//         },
//         standardError: {
//           borderRadius: '10px',
//         },
//         standardSuccess: {
//           borderRadius: '10px',
//         },
//       },
//     },
//     MuiSnackbar: {
//       styleOverrides: {
//         anchorOriginTopCenter: {
//           marginTop: '70px', // adjust for sticky header
//         },
//       },
//     },
//     MuiCard: {
//       styleOverrides: {
//         root: {
//           borderRadius: '10px',
//         },
//       },
//     },
//     MuiDialog: {
//       styleOverrides: {
//         paper: {
//           borderRadius: '10px',
//         },
//       },
//     },
//     MuiTypography: {
//       defaultProps: {
//         variantMapping: {
//           body1Bold: 'p',
//           body3: 'p',
//           body4: 'p',
//           subtitle1: 'p',
//           subtitle2: 'p',
//         },
//       },
//     },
//   },
// })

// export default theme
